import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  Divider,
  CircularProgress,
  Typography,
  Autocomplete,
  // FormControlLabel,
  // Checkbox
} from '@mui/material';

import {
  appendCashReconLedgerCoaQueryParams,
  fetchCashReconLedgerScope,
} from './cashReconLedgerScope';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateSelectMenuProps,
} from '../../../theme/harmonateStyles';

/* Sage integration disabled for Cash Recon adjustments — Postgres save only.
const summarizeSageFailure = (sage) => {
  if (!sage || typeof sage !== 'object') return 'Sage integration failed.';
  const ve = Array.isArray(sage.validationErrors) ? sage.validationErrors.filter(Boolean) : [];
  if (ve.length) {
    return `${sage.message || 'Sage validation failed'}: ${ve.join('; ')}`;
  }
  if (sage.message) return sage.message;
  if (typeof sage.error === 'string' && sage.error.trim()) {
    const m = sage.error.match(/"message":"((?:[^"\\]|\\.)*)"/);
    if (m) return m[1].replace(/\\"/g, '"');
    return sage.error.length > 400 ? `${sage.error.slice(0, 400)}…` : sage.error;
  }
  return 'Sage integration failed.';
};
*/

const ADJUSTMENT_TYPES = {
  credit: {
    label: 'Credit',
    description: 'Enter credit amount'
  },
  debit: {
    label: 'Debit',
    description: 'Enter debit amount'
  }
};

/** Local date/time for read-only Integration Date: DD-MM-YYYY, h:mm:ss am/pm (hyphens, not slashes). */
const formatIntegrationDateDisplay = (value) => {
  if (value == null || value === '') return 'N/A';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const timeStr = d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
  return `${dd}-${mm}-${yyyy}, ${timeStr}`;
};

const AdjustmentPopup = ({
  open,
  onClose,
  onSubmit,
  selectedHotel,
  hotelDetails,
  selectedMonthYear = '',
  editMode = false,
  editData = null,
  showToast,
  showError,
}) => {
  const initialFormState = {
    // date: new Date().toISOString().split('T')[0],
    date: '',
    type: '',
    docNo: '',
    glCode: '',
    bankGlCode: '',
    memo: '',
    amount: '',
    adjustmentReason: '',
    approveIntegration: false,
    integrationStatus: 'PENDING',
    integrationDate: null
  };

  // Set form data based on edit mode
  const getInitialFormData = () => {
    if (editMode && editData && editData.originalData) {
      const data = editData.originalData;
      return {
        date: data.transactionDate || '',
        type: data.creditAmount ? 'credit' : data.debitAmount ? 'debit' : '',
        docNo: data.referenceNo || '',
        glCode: data.ledgerAccountId || '',
        bankGlCode: '', // Will be set after glCodes are loaded
        memo: data.description || '',
        amount: data.creditAmount || data.debitAmount || '',
        adjustmentReason: data.adjustmentReason || '',
        approveIntegration: data.approveIntegration || false,
        integrationStatus: data.integrationStatus || 'PENDING',
        integrationDate: data.integrationDate || null
      };
    }
    return initialFormState;
  };

  const [formData, setFormData] = useState(getInitialFormData());
  const [glCodeLoading, setGlCodeLoading] = useState(false);
  const [glCodes, setGlCodes] = useState([]);
  const [mappingHeadersByProperty, setMappingHeadersByProperty] = useState([]);
  const hasSetDefaultGlRef = useRef(false);

  // Reset default-GL flag when dialog closes
  useEffect(() => {
    if (!open) hasSetDefaultGlRef.current = false;
  }, [open]);

  // Derived boolean: if already integrated, lock everything
  const isIntegrated = editMode && !!formData.integrationDate;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Fetch ledger accounts for GL Code dropdown using same API as GLTransactionDrawer
  const fetchLedgerAccounts = async (propertyId) => {
    console.log('*** fetchLedgerAccounts called with propertyId:', propertyId);
    if (!propertyId) {
      console.log('No propertyId provided, returning early');
      setGlCodes([]);
      return;
    }

    console.log('Starting fetchLedgerAccounts API call...');
    setGlCodeLoading(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      // const url = `${baseUrl}conductor/cash-recon/chart-of-account/by-tenant-with-account-type-property?tenant_id=${tenantId}&property_id=${propertyId}`;
      const url = await appendCashReconLedgerCoaQueryParams(
        `${baseUrl}conductor/cash-recon/chart-of-account/by-tenant-with-account-type-property?tenant_id=${tenantId}`
      , { baseUrl, tenantId, token });

      console.log('Making API call to:', url);

      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('API response status:', resp.status);
      console.log('API response ok:', resp.ok);

      if (!resp.ok) {
        throw new Error(`Failed to fetch ledger accounts: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();
      console.log('API response data:', data);

      if (Array.isArray(data)) {
        console.log('Setting GL codes:', data.length, 'items');
        setGlCodes(data);
      } else {
        console.log('API response is not an array, setting empty GL codes');
        setGlCodes([]);
      }
    } catch (err) {
      console.error('Error fetching ledger accounts:', err);
      setGlCodes([]);
    } finally {
      console.log('fetchLedgerAccounts completed, setting loading to false');
      setGlCodeLoading(false);
    }
  };

  // Fetch mapping headers by property for default GL Account (glAccountNo, glAccountName)
  const fetchMappingHeadersByProperty = async (propertyId) => {
    if (!propertyId) {
      setMappingHeadersByProperty([]);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      if (!token || !tenantId) return;
      const url = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon-mapping-headers/by-property?tenant_id=${tenantId}&property_id=${encodeURIComponent(propertyId)}`;
      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!resp.ok) {
        setMappingHeadersByProperty([]);
        return;
      }
      const data = await resp.json();
      setMappingHeadersByProperty(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching mapping headers by property:', err);
      setMappingHeadersByProperty([]);
    }
  };

  // Reset form data when dialog opens or editData changes
  useEffect(() => {
    if (open) {
      setFormData(getInitialFormData());
    }
  }, [open, editData, editMode]);

  // Fetch GL codes when selectedHotel changes
  useEffect(() => {
    console.log('=== AdjustmentPopup useEffect ===');
    console.log('selectedHotel:', selectedHotel);
    console.log('hotelDetails:', hotelDetails);
    console.log('hotelDetails length:', hotelDetails?.length);

    if (selectedHotel && hotelDetails) {
      console.log('Available properties:');
      hotelDetails.forEach((p, index) => {
        console.log(`${index}: PROPERTY_NAME="${p.PROPERTY_NAME}", propertyName="${p.propertyName}"`);
      });

      // Find property ID from selectedHotel name (try both fields)
      const property = hotelDetails.find(p =>
        p.PROPERTY_NAME === selectedHotel ||
        p.propertyName === selectedHotel ||
        p.PROPERTY_NAME?.trim() === selectedHotel?.trim() ||
        p.propertyName?.trim() === selectedHotel?.trim()
      );
      const propertyId = property?.PROPERTY_ID || property?.propertyId;
      console.log('Found property:', property);
      console.log('Found propertyId:', propertyId);

      if (propertyId) {
        fetchLedgerAccounts(propertyId);
        if (open) {
          fetchMappingHeadersByProperty(propertyId);
        } else {
          setMappingHeadersByProperty([]);
        }
      } else {
        console.log('No propertyId found for selectedHotel:', selectedHotel);
        setGlCodes([]);
        setMappingHeadersByProperty([]);
      }
    } else {
      console.log('selectedHotel or hotelDetails is missing.');
      console.log('selectedHotel value:', selectedHotel);
      console.log('hotelDetails exists:', !!hotelDetails);
      setGlCodes([]);
      setMappingHeadersByProperty([]);
    }
  }, [selectedHotel, hotelDetails, open]);

  // Set bankGlCode after glCodes are loaded in edit mode
  useEffect(() => {
    if (editMode && editData && editData.originalData && glCodes.length > 0) {
      const data = editData.originalData;
      if (data.bankglAccountNo) {
        const bankAccount = glCodes.find(gl => gl.glCode === data.bankglAccountNo);
        if (bankAccount) {
          setFormData(prev => ({
            ...prev,
            bankGlCode: bankAccount.ledgerAccountId
          }));
        }
      }
    }
  }, [glCodes, editMode, editData]);

  // Default GL Account from mapping header for selected property + month/year (add mode only)
  useEffect(() => {
    if (!open || editMode || hasSetDefaultGlRef.current || !selectedMonthYear || glCodes.length === 0 || mappingHeadersByProperty.length === 0) return;
    const header = mappingHeadersByProperty.find((h) => h.monthYear === selectedMonthYear && (h.glAccountNo || h.glAccountName));
    if (!header) return;
    const match = glCodes.find(
      (gl) =>
        (header.glAccountNo && String(gl.glCode || '').trim() === String(header.glAccountNo).trim()) ||
        (header.glAccountName && String(gl.glDescription || '').trim() === String(header.glAccountName).trim())
    );
    if (match) {
      hasSetDefaultGlRef.current = true;
      setFormData((prev) => ({ ...prev, glCode: match.ledgerAccountId }));
    }
  }, [open, editMode, selectedMonthYear, glCodes, mappingHeadersByProperty]);

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';

      if (!token || !tenantId) {
        throw new Error('Missing authentication or tenant information');
      }
      const { systemType, glHeaderId } = await fetchCashReconLedgerScope({ baseUrl, tenantId, token });

      // Find property ID and property code from selectedHotel name (same logic as in useEffect)
      const property = hotelDetails?.find(p =>
        p.PROPERTY_NAME === selectedHotel ||
        p.propertyName === selectedHotel ||
        p.PROPERTY_NAME?.trim() === selectedHotel?.trim() ||
        p.propertyName?.trim() === selectedHotel?.trim()
      );
      const propertyId = property?.PROPERTY_ID || property?.propertyId;
      const propertyCode = property?.PROPERTY_CODE || property?.propertyCode || '';

      // Prepare the request body for the new API format
      // Find the selected GL account object to get both ledgerAccountId and glCode
      const selectedGLAccount = glCodes.find(gl => gl.ledgerAccountId === formData.glCode);
      const selectedBankGLAccount = glCodes.find(gl => gl.ledgerAccountId === formData.bankGlCode);

      const amountValue = parseFloat(formData.amount);
      const requestBody = {
        ledgerAccountId: selectedGLAccount?.ledgerAccountId || formData.glCode, // Use actual ledgerAccountId UUID
        transactionDate: formData.date,
        referenceNo: formData.docNo,
        description: formData.memo,
        debitAmount: formData.type === 'debit' ? amountValue : null,
        creditAmount: formData.type === 'credit' ? amountValue : null,
        amount: amountValue,
        tenantId: tenantId,
        propertyId: propertyId || null,
        propertyCode: propertyCode || '', // Added property code
        glAccountNo: selectedGLAccount?.glCode || '', // Use glCode string
        glAccountName: selectedGLAccount?.glDescription || '', // Use glDescription string
        glAccountType: selectedGLAccount?.normalBalance ?? null,
        bankglAccountNo: selectedBankGLAccount?.glCode || '',
        bankglAccountName: selectedBankGLAccount?.glDescription || '',
        bankAccountType: selectedBankGLAccount?.normalBalance ?? null,
        transactionType: "ADJUSTMENT",
        sourceSystem: "MANUAL",
        systemType,
        glHeaderId,
        isAdjustment: true,
        adjustmentReason: formData.adjustmentReason || null,
        // Sage integration disabled — always save to Postgres only
        approveIntegration: false,
        // approveIntegration: formData.approveIntegration,
        integrationStatus: editMode ? formData.integrationStatus : 'PENDING',
        externalTxnId: editMode ? (editData?.originalData?.externalTxnId ?? null) : null,
        ...(editMode && { integrationDate: formData.integrationDate })
      };

      const apiUrl = editMode
        ? `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/gl-transactions/${editData.originalData.id}`
        : `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/gl-transactions`;

      const response = await fetch(apiUrl, {
        method: editMode ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        const msg =
          result.message ||
          result.error ||
          `Failed to save adjustment: ${response.status} ${response.statusText}`;
        showError?.(msg);
        return;
      }

      // Sage integration disabled — Postgres save only
      showToast?.({
        message: editMode ? 'Adjustment updated.' : 'Adjustment saved.',
        severity: 'success',
        autoHideDuration: 10000,
      });
      /* Sage toast handling disabled
      const sage = result.sageResponse;
      const sageFailed = sage && sage.success === false;
      const sageOk = !sageFailed;

      if (sageOk) {
        let successMsg = editMode ? 'Adjustment updated.' : 'Adjustment saved.';
        if (sage?.success === true && sage.message) {
          successMsg += ` ${sage.message}`;
          if (sage.journalEntryId != null && sage.journalEntryId !== '') {
            successMsg += ` (Journal entry: ${sage.journalEntryId})`;
          }
        } else if (result.integrationStatus === 'COMPLETED') {
          successMsg += ' Sage integration completed.';
        }
        showToast?.({
          message: successMsg,
          severity: 'success',
          autoHideDuration: 10000,
        });
      } else {
        const detail = summarizeSageFailure(sage);
        showToast?.({
          message: `Saved in Cash Reconciliation, but Sage failed. ${detail}`,
          severity: 'warning',
          autoHideDuration: 22000,
        });
      }
      */

      onSubmit({
        ...formData,
        amount: parseFloat(formData.amount),
        editMode,
        id: editData?.originalData?.id,
      });

      setFormData(initialFormState);
      onClose();
    } catch (err) {
      console.error('Error saving adjustment:', err);
      showError?.(err.message || 'Failed to save adjustment');
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData(initialFormState);
    onClose();
  };

  // Add a separate effect to handle form reset when closing
  useEffect(() => {
    if (!open) {
      setFormData(initialFormState);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { ...harmonateDialogPaperProps.sx },
      }}
    >
      <DialogTitle sx={{ ...harmonateDialogTitleSx, pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {editMode ? 'Edit Adjustment' : 'Add Adjustment'}
          </Typography>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={harmonateDialogFormContentSx}>
        <Box sx={{ ...harmonateFormFieldsSx, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Row 1: Property Name + Posted Date */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Property Name"
              value={selectedHotel || ''}
              fullWidth
              disabled
              size="small"
              placeholder="No property selected"
              sx={{ flex: 1 }}
            />
            <TextField
              name="date"
              label="Posted Date"
              type="date"
              value={formData.date}
              onChange={handleChange}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1, minWidth: 130 }}
              disabled={isIntegrated}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Autocomplete
                disabled={isIntegrated || glCodeLoading || !!formData.glCode}
                options={glCodes}
                getOptionLabel={(option) => {
                  if (!option) return '';
                  const no = option.glCode || option.glAccountNo || '';
                  const name = option.glDescription || option.glAccountName || option.glAccount || '';
                  return no && name ? `${no} - ${name}` : name || no || '';
                }}
                value={glCodes.find(gl => gl.ledgerAccountId === formData.glCode) || null}
                onChange={(event, newValue) => {
                  setFormData(prev => ({
                    ...prev,
                    glCode: newValue ? newValue.ledgerAccountId : ''
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Operating Account"
                    size="small"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {glCodeLoading ? (
                            <CircularProgress size={20} />
                          ) : (
                            params.InputProps.endAdornment
                          )}
                        </>
                      ),
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.ledgerAccountId === value.ledgerAccountId}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Autocomplete
                disabled={isIntegrated || glCodeLoading}
                options={glCodes}
                getOptionLabel={(option) => option.glAccount || ''}
                value={glCodes.find(gl => gl.ledgerAccountId === formData.bankGlCode) || null}
                onChange={(event, newValue) => {
                  setFormData(prev => ({
                    ...prev,
                    bankGlCode: newValue ? newValue.ledgerAccountId : ''
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="GL Account"
                    size="small"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {glCodeLoading ? (
                            <CircularProgress size={20} />
                          ) : (
                            params.InputProps.endAdornment
                          )}
                        </>
                      ),
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.ledgerAccountId === value.ledgerAccountId}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth size="small" required disabled={isIntegrated} sx={{ flex: 1 }}>
              <InputLabel id="adjustment-bank-type-label" shrink>
                Bank Type
              </InputLabel>
              <Select
                labelId="adjustment-bank-type-label"
                name="type"
                value={formData.type}
                label="Bank Type"
                onChange={handleChange}
                displayEmpty
                MenuProps={harmonateSelectMenuProps}
              >
                <MenuItem value="">
                  <em>Select Type</em>
                </MenuItem>
                {Object.entries(ADJUSTMENT_TYPES).map(([key, { label }]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              name="amount"
              label={formData.type === 'credit' ? 'Credit Amount' : 'Debit Amount'}
              type="number"
              value={formData.amount}
              onChange={handleChange}
              fullWidth
              required
              size="small"
              sx={{ flex: 1 }}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography>,
              }}
              inputProps={{ step: '0.01', min: '0' }}
              disabled={isIntegrated}
            />
          </Box>

          <TextField
            name="docNo"
            label="Doc No"
            value={formData.docNo}
            onChange={handleChange}
            fullWidth
            size="small"
            placeholder="Enter document number..."
            disabled={isIntegrated}
          />

          <TextField
            name="memo"
            label="Memo / Description"
            value={formData.memo}
            onChange={handleChange}
            fullWidth
            multiline
            rows={3}
            size="small"
            placeholder="Enter memo or description..."
            disabled={isIntegrated}
          />

          <TextField
            name="adjustmentReason"
            label="Adjustment Reason"
            value={formData.adjustmentReason}
            onChange={handleChange}
            fullWidth
            multiline
            rows={3}
            size="small"
            placeholder="Enter adjustment reason..."
            disabled={isIntegrated}
          />

          {/* Sage integration disabled — Approve Integration checkbox removed
          <FormControlLabel
            control={
              <Checkbox
                name="approveIntegration"
                checked={formData.approveIntegration}
                onChange={handleChange}
                color="primary"
                sx={{ '&.Mui-checked': { color: ht.green } }}
                disabled={isIntegrated}
              />
            }
            label="Approve Integration"
            sx={{ m: 0, alignSelf: 'flex-start' }}
          />
          */}

          {editMode && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Integration Status"
                value={formData.integrationStatus || 'N/A'}
                fullWidth
                size="small"
                disabled
                sx={{ flex: 1 }}
              />
              <TextField
                label="Integration Date"
                value={formatIntegrationDateDisplay(formData.integrationDate)}
                fullWidth
                size="small"
                disabled
                sx={{ flex: 1 }}
              />
            </Box>
          )}

        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} sx={harmonateCancelButtonSx}>
          {isIntegrated ? 'Close' : 'Cancel'}
        </Button>
        {!isIntegrated && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={glCodeLoading}
            sx={harmonateProceedButtonSx}
          >
            {editMode ? 'Update' : 'Add'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AdjustmentPopup;